import { SocialMediaRulesConfig, EquipmentRulesConfig } from '../../store/usePEPapersStore';

export interface InstructionAssemblerParams {
  socialMedia: SocialMediaRulesConfig;
  equipment: EquipmentRulesConfig;
  customRulesText?: string;
}

export interface DocumentSection {
  title: string;
  content: string;
}

export interface InstructionDocumentNaming {
  headerTitle: string;
  archiveLabel: string;
  pdfFilenameStem: string;
  subtitle: string;
}

export interface AssembleInstructionResult {
  sections: DocumentSection[];
  headerTitle: string;
  archiveLabel: string;
  pdfFilenameStem: string;
  subtitle: string;
}

export const INSTRUCTION_DOCUMENT_NAMING: InstructionDocumentNaming = {
  headerTitle: 'DIENSTANWEISUNG SOCIAL MEDIA & SALON-EQUIPMENT',
  archiveLabel: 'Dienstanweisung Social Media & Equipment',
  pdfFilenameStem: 'Dienstanweisung_Social_Media_Equipment',
  subtitle: 'SOCIAL MEDIA & SALON-EQUIPMENT',
};

export function assembleInstructionSections(params: InstructionAssemblerParams): AssembleInstructionResult {
  const sections: DocumentSection[] = [];
  let sectionIndex = 1;

  // § Social Media 1: Privates Smartphone
  if (params.socialMedia.noPrivatePhoneAtWork) {
    sections.push({
      title: `§ ${sectionIndex++} Private Smartphone-Nutzung`,
      content: `Die private Nutzung von Smartphones, Smartwatches und sonstigen mobilen Endgeräten während der aktiven Arbeitszeit und insbesondere während der Kundenbedienung ist untersagt. Private Geräte sind während der Bedienungszeiten stummgeschaltet im Personalbereich zu verwahren.`,
    });
  }

  // § Social Media 2: Private Kunden-Posts
  if (params.socialMedia.noPrivateCustomerPosts) {
    sections.push({
      title: `§ ${sectionIndex++} Bildrechte & Veröffentlichungen von Kundenarbeiten`,
      content: `Das Anfertigen von Foto- oder Videoaufnahmen von Kunden sowie deren Veröffentlichung auf privaten Mitarbeiter-Profilen oder privaten Social-Media-Accounts ist strengstens untersagt. Sämtliche Arbeitsergebnisse unterliegen dem Urheber- und Hausrecht des Salons.`,
    });
  }

  // § Social Media 3: Offizielle Salon-Kanäle
  if (params.socialMedia.onlyOfficialAccountsAllowed) {
    sections.push({
      title: `§ ${sectionIndex++} Exklusive Nutzung offizieller Salon-Kanäle`,
      content: `Content-Erstellung (Vorher-Nachher-Bilder, Stories, Reels) darf ausschließlich im Auftrag des Salons und zur Veröffentlichung auf den offiziellen Salon-Accounts erfolgen. Zugangsdaten zu Salon-Profilen sind vertraulich zu behandeln.`,
    });
  }

  // § Social Media 4: Datenschutz & Kunden-DMs
  if (params.socialMedia.strictDataPrivacyNoDMs) {
    sections.push({
      title: `§ ${sectionIndex++} Datenschutz & Kundenkommunikation (DSGVO)`,
      content: `Die Kommunikation mit Kunden über private Messenger-Dienste (wie WhatsApp, Instagram Direct Message oder Facebook Messenger) bezüglich Terminvereinbarungen, Rezepturen, Beratungen oder Preisen ist ausnahmslos untersagt. Alle Kundenkontakte haben über die offiziellen Salon-Kommunikationswege zu laufen.`,
    });
  }

  // § Equipment 1: Hardware-Nutzung
  if (params.equipment.ipadBusinessOnly) {
    sections.push({
      title: `§ ${sectionIndex++} Salon-Hardware & IT-Systeme`,
      content: `Überlassene Tablets (z. B. Salon-iPads), Kassenhardware und Terminals dürfen ausschließlich für betriebliche Zwecke (Terminbuchung, Kassenführung, digitale Kundenkartei, Farbberatung) genutzt werden. Private Downloads, private Account-Logins oder privates Surfen sind untersagt.`,
    });
  }

  // § Equipment 2: Mitnahmeverbot (nur Salon-Eigentum; Privatwerkzeug ausgenommen)
  if (params.equipment.noToolsHomeTaking) {
    sections.push({
      title: `§ ${sectionIndex++} Verbleib von Salon-Werkzeugen & Arbeitsmitteln`,
      content: `Sämtliche vom Salon bereitgestellte bzw. im Eigentum des Salons stehende Werkzeuge, Scheren, Maschinen, Föhne, Kosmetikgeräte und Verbrauchsmaterialien verbleiben dauerhaft in den Salonräumlichkeiten. Eine Mitnahme dieses Salon-Eigentums für private Zwecke außerhalb der Arbeitszeit ist untersagt. Privates Werkzeug und Arbeitsmittel, die der Mitarbeiter selbst mitbringt und die in seinem Eigentum stehen, sind von diesem Mitnahmeverbot ausdrücklich ausgenommen.`,
    });
  }

  // § Equipment 3: Sorgfalt & Haftung
  if (params.equipment.careAndLiabilityDuty) {
    sections.push({
      title: `§ ${sectionIndex++} Sorgfaltspflicht, Pflege & Schadenshaftung`,
      content: `Alle Arbeitsmittel sind pfleglich zu behandeln, regelmäßig nach Hygienerichtlinien zu reinigen und vor Beschädigung zu schützen. Beschädigungen oder Defekte sind der Salonleitung unverzüglich zu melden. Bei grob fahrlässiger oder vorsätzlicher Beschädigung behält sich der Arbeitgeber Regressansprüche vor.`,
    });
  }

  // § Equipment 4: Rückgabepflicht
  if (params.equipment.returnOnTermination) {
    sections.push({
      title: `§ ${sectionIndex++} Rückgabepflicht bei Beendigung des Arbeitsverhältnisses`,
      content: `Im Falle der Beendigung des Arbeitsverhältnisses oder bei Freistellung sind sämtliche überlassenen Arbeitsmittel, Hardwarekomponenten, Schlüssel, Zugangsmedien und Handbücher unaufgefordert bis spätestens zum letzten tatsächlichen Arbeitstag in einwandfreiem Zustand zurückzugeben.`,
    });
  }

  // § Zusatzabsprachen
  if (params.customRulesText && params.customRulesText.trim().length > 0) {
    sections.push({
      title: `§ ${sectionIndex++} Individuelle Zusatzvereinbarungen`,
      content: params.customRulesText.trim(),
    });
  }

  // § Schlussbestimmungen
  sections.push({
    title: `§ ${sectionIndex++} Arbeitsrechtliche Verbindlichkeit & Schlussbestimmungen`,
    content: `Diese Dienstanweisung ist Bestandteil der arbeitsvertraglichen Pflichten. Vorsätzliche oder wiederholte Verstöße können arbeitsrechtliche Maßnahmen bis hin zur Abmahnung oder Kündigung nach sich ziehen. Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.`,
  });

  return {
    sections,
    ...INSTRUCTION_DOCUMENT_NAMING,
  };
}
